import { Cancel, CheckCircle, ChevronLeft } from '@mui/icons-material';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import MoreVert from '@mui/icons-material/MoreVert';
import PercentIcon from '@mui/icons-material/Percent';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Avatar, Box, Card, CardContent } from '@mui/material';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Dropdown from '../../../components/Dropdown';
import { LoadingContent } from '../../../components/Loading';
import useDialog from '../../../hooks/useDialog';
import { useFetch } from '../../../hooks/useFecth';
import DialogAdvertiser from './components/DialogAdvertiser';
import ListBranches from './components/ListBranches';
import ListOffers from './components/ListOffers';
import ListAdvertiserUsers from './components/ListAdvertiserUsers';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import ConfirmationModal from '../../../components/ConfirmationModal';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useFormik } from 'formik';
import FileUpload from '../../../components/FileUpload';
import DataGridJoy from '../../../components/DataGridJoy';
import ClubTabLayout from '../Clubs/components/ClubTabLayout';

function MetaField({ label, children }) {
    return (
        <Box minWidth={0}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5, fontWeight: 700, letterSpacing: 0.2 }}
            >
                {label}
            </Typography>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" fontWeight={700} noWrap title={String(children)}>
                    {children}
                </Typography>
            ) : (
                children
            )}
        </Box>
    );
}

function getContractChipProps(contract) {
    if (!contract) return { label: 'Sem contrato', color: 'default' };
    const status = String(contract.status || '').toUpperCase();
    if (contract.signedAt || ['SIGNED', 'CONCLUDED'].includes(status)) {
        const date = contract.signedAt ? ` em ${new Date(contract.signedAt).toLocaleDateString('pt-BR')}` : '';
        return { label: `Contrato assinado${date}`, color: 'success' };
    }
    if (status === 'PENDING' || contract.integrationId) {
        return { label: 'Contrato gerado · aguardando assinatura', color: 'warning' };
    }
    return { label: 'Só prévia do contrato', color: 'default' };
}

function AdvertiserContractPanel({ advertiser }) {
    const contract = advertiser?.contract;
    const uploads = (contract?.Uploads || []).filter(i => !i.deletedAt);
    const signedPdfUrl = uploads.find(i => i.description === 'contrato_assinado')?.fileUrl;
    const originalPdfUrl = uploads.find(i => i.description === 'contrato_original')?.fileUrl;
    const hasContractSigned =
        Boolean(contract?.signedAt) || ['CONCLUDED', 'SIGNED'].includes(contract?.status);

    return (
        <ClubTabLayout>
            <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                    {!contract ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhum contrato vinculado a este anunciante.
                        </Typography>
                    ) : (
                        <>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                <Typography variant="body2" color="text.secondary">
                                    Status:
                                </Typography>
                                <Chip
                                    size="small"
                                    label={contract.status || (hasContractSigned ? 'Assinado' : 'Pendente')}
                                    color={hasContractSigned ? 'success' : 'default'}
                                    variant="outlined"
                                />
                                {contract.signedAt ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Assinado em {new Date(contract.signedAt).toLocaleString('pt-BR')}
                                    </Typography>
                                ) : null}
                            </Stack>
                            {contract.id != null ? (
                                <Typography variant="body2" color="text.secondary">
                                    Contrato #{contract.id}
                                </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {signedPdfUrl ? (
                                    <Button variant="contained" component="a" href={signedPdfUrl} target="_blank" rel="noopener noreferrer" download>
                                        Baixar contrato assinado (PDF)
                                    </Button>
                                ) : null}
                                {originalPdfUrl ? (
                                    <Button variant="outlined" component="a" href={originalPdfUrl} target="_blank" rel="noopener noreferrer" download>
                                        Contrato original (PDF)
                                    </Button>
                                ) : null}
                            </Stack>
                            {!signedPdfUrl ? (
                                <Typography variant="body2" color="text.secondary">
                                    O PDF do contrato assinado ainda não está disponível nos uploads.
                                </Typography>
                            ) : null}
                        </>
                    )}
                </Stack>
            </Box>
        </ClubTabLayout>
    );
}

export default function AdvertiserDetails() {
    const { id } = useParams();
    const { data, error, mutate, isFetching } = useFetch(`advertiser/${id}`);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const { setErrors, enqueueSnackbar } = useErrors();
    const navigate = useNavigate();

    const [tab, setTab] = useState('#tab-1');

    if (isFetching) return <LoadingContent />;
    if (error) return <>Error: {error?.message}</>;
    if (!data) return <>Tente novamente mais tarde</>;

    const handleDelete = () => {
        setConfirmAction(() => async () => {
            try {
                await server.delete(`advertiser/${id}`);
                enqueueSnackbar('Anunciante deletado com sucesso!');
                navigate('/dashboard/anunciantes'); // Redireciona para a lista de anunciantes
            } catch (error) {
                setErrors('Erro ao deletar anunciante.', error);
            }
            setConfirmOpen(false);
        });
        setConfirmOpen(true);
    };

    return (
        <>
            <Container maxWidth="xl" sx={{ gap: 3, flexDirection: 'column', display: 'flex' }}>
                <Stack alignItems="center" direction="row" columnGap={1} pb={2}>
                    <IconButton component={Link} to={-1} replace={true}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h2">Detalhes do anunciante</Typography>
                </Stack>

                <Card>
                <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, '&:last-child': { pb: 2.5 } }}>
                    <Stack direction="row" alignItems="flex-start" gap={2}>
                        <Avatar
                            variant="rounded"
                            src={data.image}
                            alt={data?.name}
                            sx={{
                                width: 112,
                                height: 72,
                                borderRadius: 2,
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& img': { objectFit: 'contain' },
                            }}
                        />
                        <Box flex={1} minWidth={0}>
                            <Typography variant="h4">{data.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                #{data.id}
                                {data.businessName ? ` · ${data.businessName}` : ''}
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 1, minWidth: 0 }}>
                                <Box
                                    title={data.status ? 'Anunciante ativo' : 'Anunciante inativo'}
                                    aria-label={data.status ? 'Anunciante ativo' : 'Anunciante inativo'}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        bgcolor: data.status ? 'success.main' : 'text.disabled',
                                    }}
                                />
                                <Typography variant="body2" fontWeight={700} noWrap>
                                    {data.status ? 'Ativo' : 'Inativo'}
                                    {data.approved ? ' · Aprovado' : ' · Aguardando aprovação'}
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={<DescriptionIcon />}
                                    {...getContractChipProps(data.contract)}
                                    onClick={() => setTab('#tab-5')}
                                />
                                {data.Club ? (
                                    <Typography
                                        component={Link}
                                        to={`/dashboard/clubes/${data.clubId}`}
                                        variant="body2"
                                        color="primary"
                                        noWrap
                                        sx={{ textDecoration: 'none' }}
                                    >
                                        Indicado por {data.Club.name}
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Box>
                        <Dropdown>
                            <IconButton>
                                <MoreVert />
                            </IconButton>
                            <Menu placement="bottom-end">
                                <MenuItem component={Link} to={`#advertiser|${id}`}>
                                    Alterar dados
                                </MenuItem>
                                <Divider />
                                <MenuItem disabled>ID legado: {data.originId || '—'}</MenuItem>
                            </Menu>
                        </Dropdown>
                    </Stack>

                    <Divider />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 2,
                        }}
                    >
                        <MetaField label="Documento">{data.document || '--'}</MetaField>
                        <MetaField label="Responsável">{data.responsibleName || '--'}</MetaField>
                        <MetaField label="Contato">{data.responsibleEmail || data.email || data.phone || '--'}</MetaField>
                    </Box>
                </CardContent>
            </Card>

            <TabContext value={tab}>
                <Stack spacing={2}>
                    <Card>
                        <TabList
                            onChange={(e, next) => setTab(next)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                px: 1,
                                minHeight: 56,
                                '& .MuiTab-root': { minHeight: 56 },
                            }}
                        >
                            <Tab icon={<PercentIcon />} iconPosition="start" label="Ofertas" value="#tab-1" />
                            <Tab icon={<CorporateFareIcon />} iconPosition="start" label="Filiais" value="#tab-3" />
                            <Tab icon={<FileCopyIcon />} iconPosition="start" label="Arquivos" value="#tab-4" />
                            <Tab icon={<DescriptionIcon />} iconPosition="start" label="Contrato" value="#tab-5" />
                            <Tab icon={<PeopleAltRounded />} iconPosition="start" label="Usuários" value="#tab-users" />
                        </TabList>
                    </Card>
                    <TabPanel value="#tab-1" sx={{ p: 0 }}>
                        <ListOffers advertiser={data} />
                    </TabPanel>
                    <TabPanel value="#tab-3" sx={{ p: 0 }}>
                        <ListBranches />
                    </TabPanel>
                    <TabPanel value="#tab-4" sx={{ p: 0 }}>
                        <DialogUploadFile advertiser={data} />
                    </TabPanel>
                    <TabPanel value="#tab-5" sx={{ p: 0 }}>
                        <AdvertiserContractPanel advertiser={data} />
                    </TabPanel>
                    <TabPanel value="#tab-users" sx={{ p: 0 }}>
                        <ListAdvertiserUsers advertiserId={data?.id} />
                    </TabPanel>
                </Stack>
            </TabContext>

                <DialogAdvertiser mutate={mutate} />
                <ConfirmationModal
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={confirmAction}
                    title="Confirmação de Exclusão"
                    message="Tem certeza de que deseja excluir este anunciante?"
                />
            </Container>
        </>
    );
}

const categories = [
    { value: 'contrato', label: 'Contrato' },
    { value: 'documento', label: 'Documento' },
    { value: 'anexo', label: 'Anexo' },
    { value: 'comprovante', label: 'Comprovante' },
    { value: 'outros', label: 'Outros' },
];

function DialogUploadFile({ advertiser, mutate }) {
    const { handleOpen } = useDialog('file');
    const [searchParams, setSearchParams] = useSearchParams();

    const { data, mutate: mutateFiles } = useFetch(`advertiser/${advertiser?.id}/files`, {
        enabled: !!advertiser?.id,
    });

    const handleChangePage = useCallback(
        value => {
            setSearchParams(prev => ({
                ...Object.fromEntries(prev),
                page: value,
            }));
        },
        [setSearchParams],
    );

    // Atualização de número de linhas por página
    const handleChangeRowsPerPage = useCallback(
        value => {
            setSearchParams(prev => ({
                ...Object.fromEntries(prev),
                perPage: value,
            }));
        },
        [setSearchParams],
    );

    useEffect(() => {
        mutateFiles();
    }, [searchParams, mutate]);

    return (
        <ClubTabLayout
            actions={
                <Button variant="outlined" component={Link} to="#file">
                    Adicionar arquivo
                </Button>
            }
        >
            <DataGridJoy
                rows={data?.rows || []}
                page={data?.page || 0}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                rowsPerPage={data?.rowsPerPage || 10}
                numRows={data?.numRows || 0}
                columns={[
                    { field: 'id', headerName: '#ID', width: 10 },
                    { field: 'fileName', headerName: 'Titulo', flex: 1 },
                    {
                        field: 'category',
                        headerName: 'Categoria',
                        type: 'string',
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        type: 'boolean',
                        render: ({ row }) => (row.status ? <CheckCircle color="success" /> : <Cancel color="error" />),
                    },
                    {
                        field: 'action',
                        headerName: 'Ações',
                        type: 'action',
                        render: ({ row }) => (
                            <IconButton size="small" component={Link} to={`#file|${row.id}`} onClick={e => handleOpen(row.id)}>
                                <EditIcon />
                            </IconButton>
                        ),
                    },
                ]}
            />
            <DialogFile mutate={mutate} mutateFiles={mutateFiles} advertiser={advertiser} />
        </ClubTabLayout>
    );
}

function AddFileModal({ advertiser, mutate, mutateFiles, file }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileBase64, setFileBase64] = useState(null);
    const [fileCategory, setFileCategory] = useState('');
    const [fileDescription, setFileDescription] = useState('');
    const { enqueueSnackbar, setErrors } = useErrors();
    const { open, handleClose, id: fileId } = useDialog('#file');
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const { handleSubmit, setFieldValue, errors, isSubmitting } = useFormik({
        initialValues: {
            fileName: '',
            fileUrl: '',
            category: '',
            description: '',
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                const payload = {
                    fileName: values.fileName,
                    fileUrl: values.fileUrl,
                    category: fileCategory || values.category,
                    description: fileDescription || values.description,
                    fileBase64: fileBase64,
                };

                await server.post(`advertiser/${advertiser?.id}/files`, payload);
                enqueueSnackbar(`Arquivo adicionado com sucesso.`);
                if (mutateFiles) await mutateFiles();
                if (mutate) await mutate();
                setSubmitting(false);
                handleClose();
                handleReset();
            } catch (error) {
                console.error('Error uploading file:', error);
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    const handleReset = () => {
        setSelectedFile(null);
        setFileBase64(null);
        setFileCategory('');
        setFileDescription('');
        setFieldValue('fileName', '');
        setFieldValue('fileUrl', '');
        setFieldValue('category', '');
        setFieldValue('description', '');
    };

    const handleFileSelect = fileData => {
        setSelectedFile(fileData.file);
        setFileBase64(fileData.fileBase64);
        setFileCategory(fileData.category || '');
        setFileDescription(fileData.description || '');
        setFieldValue('fileName', fileData.fileName);
    };

    const handleCategoryChange = value => {
        setFileCategory(value);
    };

    const handleDescriptionChange = value => {
        setFileDescription(value);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFileBase64(null);
        setFileCategory('');
        setFileDescription('');
        setFieldValue('fileName', '');
    };

    const handleDelete = async () => {
        try {
            await server.delete(`advertiser/${advertiser?.id}/files/${fileId || file?.id}`);
            enqueueSnackbar('Arquivo excluído com sucesso!');
            if (mutateFiles) await mutateFiles();
            if (mutate) await mutate();
            handleClose();
            setConfirmDeleteOpen(false);
        } catch (error) {
            setErrors('Erro ao excluir arquivo.', error);
            setConfirmDeleteOpen(false);
        }
    };

    return (
        <>
            <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
                {file ? (
                    <DialogTitle>Detalhes Arquivo</DialogTitle>
                ) : (
                    <DialogTitle>Adicionar Arquivo</DialogTitle>
                )}

                {!file ? (
                <>
                    <form onSubmit={handleSubmit}>
                        <DialogContent>
                            <FileUpload
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                                categories={categories}
                                showCategory={true}
                                showDescription={true}
                                onFileSelect={handleFileSelect}
                                onRemove={handleRemoveFile}
                                onCategoryChange={handleCategoryChange}
                                onDescriptionChange={handleDescriptionChange}
                                selectedFile={selectedFile}
                                fileBase64={fileBase64}
                                category={fileCategory}
                                description={fileDescription}
                                inputId="advertiser-file-input"
                                disabled={isSubmitting}
                                required={true}
                                error={!!errors.fileName}
                                helperText={errors.fileName}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    handleClose();
                                    handleReset();
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" variant="contained" disabled={isSubmitting || !selectedFile}>
                                Adicionar
                            </Button>
                        </DialogActions>
                    </form>
                </>
            ) : (
                <DialogContent>
                    {file ? (
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                {/* <Typography variant="h5" gutterBottom>
                                    Detalhes do Arquivo
                                </Typography> 
                                <Divider sx={{ my: 2 }} />*/}
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="title-sm" color="text.secondary">
                                            Título
                                        </Typography>
                                        <Typography variant="text-sm">{file.fileName || '--'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="title-sm" color="text.secondary">
                                            Categoria
                                        </Typography>
                                        <Typography variant="text-sm">
                                            {file.category ? categories.find(c => c.value === file.category)?.label || file.category : '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="title-sm" color="text.secondary">
                                            Status
                                        </Typography>
                                        <Box mt={1}>
                                            <Chip
                                                size="small"
                                                label={file.status ? 'Ativo' : 'Inativo'}
                                                color={file.status ? 'success' : 'error'}
                                                icon={<FiberManualRecordIcon />}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="title-sm" color="text.secondary">
                                            URL
                                        </Typography>
                                        <Typography
                                            variant="text-sm"
                                            sx={{
                                                wordBreak: 'break-all',
                                                color: 'primary.main',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                            }}
                                            component="a"
                                            href={file.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {file.fileUrl || '--'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="title-sm" color="text.secondary">
                                            Criado em
                                        </Typography>
                                        <Typography variant="text-sm">
                                            {file.createdAt
                                                ? new Date(file.createdAt).toLocaleString('pt-BR', {
                                                      day: '2-digit',
                                                      month: '2-digit',
                                                      year: 'numeric',
                                                      hour: '2-digit',
                                                      minute: '2-digit',
                                                  })
                                                : '--'}
                                        </Typography>
                                    </Grid>
                                    {file.description && (
                                        <Grid item xs={12}>
                                            <Typography variant="title-sm" color="text.secondary">
                                                Descrição
                                            </Typography>
                                            <Typography variant="text-sm">{file.description}</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Carregando detalhes do arquivo...
                        </Typography>
                    )}
                </DialogContent>
                )}

                {file && (
                    <DialogActions>
                        <Button onClick={() => setConfirmDeleteOpen(true)} color="error" variant="contained">
                            Excluir
                        </Button>
                        <Button onClick={handleClose} variant="outlined">
                            Fechar
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <ConfirmationModal
                open={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={handleDelete}
                title="Confirmação de Exclusão"
                message="Tem certeza de que deseja excluir este arquivo? Esta ação não pode ser desfeita."
            />
        </>
    );
}

function DialogFile({ mutate, mutateFiles, advertiser }) {
    const { open, id } = useDialog('#file');
    if (open && id) return <EditFile id={id} mutate={mutate} mutateFiles={mutateFiles} advertiser={advertiser} />;
    if (open) return <AddFileModal mutate={mutate} mutateFiles={mutateFiles} advertiser={advertiser} />;
}

function EditFile({ id, advertiser, mutateFiles }) {
    // const { id: advertiseId } = useParams();
    console.log('fileId: ', id);
    const { data, mutate } = useFetch(`advertiser/${advertiser?.id}/files/${id}`, {
        enabled: !!advertiser?.id,
    });
    return <AddFileModal file={data} mutate={mutate} mutateFiles={mutateFiles} advertiser={advertiser} />;
}
